import { useEffect, useState } from "react";
import { Badge, Placeholder } from "react-bootstrap";
import { Helmet } from "react-helmet";
import { useNavigate, useParams } from "react-router-dom";

import DstButton from "../../components/DstButton/DstButton";
import InfoRow from "../../components/InfoRow/InfoRow";
import TruncatedText from "../../components/TruncatedText/TruncatedText";
import { useSession } from "../../contexts/SessionContext";
import translate from "../../services/Translate";
import { completeNavigate } from "../../services/UtilityService";
import { fetchProgram } from "./Program.function";

const Program = () => {
  const { language } = useSession();
  const navigate = useNavigate();
  const { program_id } = useParams();
  const [loading, setLoading] = useState(true);
  const [program, setProgram] = useState<any>(null);

  useEffect(() => {
    if (program_id) {
      fetchProgram(program_id, language, setProgram, setLoading);
    }
  }, [language, program_id]);

  if (loading) {
    return (
      <div className="m-5 position-relative">
        <div className="d-flex align-items-center mb-3">
          <div
            className="dst-icon-chevron-left p-2 medium-gray-background rounded me-2 clickable"
            onClick={() => navigate("/programs")}
          />
          <Placeholder as="h2" animation="glow" className="mb-0">
            <Placeholder xs={4} />
          </Placeholder>
        </div>
        <div className="p-3">
          <Placeholder as="p" animation="glow" className="mb-2">
            <Placeholder xs={8} />
          </Placeholder>
          <Placeholder as="p" animation="glow" className="mb-0">
            <Placeholder xs={6} />
          </Placeholder>
        </div>
      </div>
    );
  }

  if (!program) {
    return (
      <div className="m-5 position-relative">
        <div className="d-flex align-items-center mb-3">
          <div
            className="dst-icon-chevron-left p-2 medium-gray-background rounded me-2 clickable"
            onClick={() => navigate("/programs")}
          />
          <h2 className="mb-0">{translate(language, "PAGES.PROGRAM.TITLE")}</h2>
        </div>
        <div className="p-3">
          <p className="mb-0">{translate(language, "PAGES.PROGRAM.ERRORS.NO_DATA")}</p>
          <DstButton
            btnClass="mt-3"
            value={translate(language, "PAGES.PROGRAM.BACK_TO_ALL")}
            variant="primary"
            clickFunction={(event: React.MouseEvent) => completeNavigate(event, navigate, "/programs")}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="m-5 position-relative">
      <Helmet>
        <title>
          {program.name} - {translate(language, "PAGES.PROGRAM.TITLE")}
        </title>
      </Helmet>
      <div className="d-flex align-items-center mb-3">
        <div
          className="dst-icon-chevron-left p-2 medium-gray-background rounded me-2 clickable"
          onClick={() => navigate("/programs")}
        />
        <h2 className="mb-0">{program.name}</h2>
      </div>
      <div className="p-3">
        {InfoRow(
          "dst-icon-hash-01",
          translate(language, "PAGES.PROGRAM.FIELDS.ID"),
          <TruncatedText text={String(program.id ?? "")} lines={3} customClassName="text-break" />
        )}
        {InfoRow(
          "dst-icon-tag-01",
          translate(language, "PAGES.PROGRAM.FIELDS.NAME"),
          <TruncatedText text={program.name ?? ""} lines={3} customClassName="text-break" />
        )}
        {InfoRow(
          "dst-icon-file-02",
          translate(language, "PAGES.PROGRAM.FIELDS.DESCRIPTION"),
          <TruncatedText text={program.description || "N/A"} lines={3} customClassName="text-break" />
        )}
        {InfoRow("dst-icon-translate-01", translate(language, "PAGES.PROGRAM.FIELDS.LANGUAGE"), program.language)}
        {InfoRow(
          "dst-icon-globe-03",
          translate(language, "PAGES.PROGRAM.FIELDS.EXTERNAL"),
          program.is_external ? (
            <Badge bg="secondary">{translate(language, "PAGES.PROGRAM.VALUES.EXTERNAL")}</Badge>
          ) : (
            <Badge bg="success">{translate(language, "PAGES.PROGRAM.VALUES.INTERNAL")}</Badge>
          )
        )}
        {InfoRow("dst-icon-user-01", translate(language, "PAGES.PROGRAM.FIELDS.MANAGER"), program.manager?.full_name)}
        {InfoRow("dst-icon-building-01", translate(language, "PAGES.PROGRAM.FIELDS.POLE"), program.pole?.name)}
      </div>
    </div>
  );
};

export default Program;
